import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { openChat } from "@/lib/chat";
import ArticleCover from "@/components/ArticleCover";

function SubscribeBox({ slug }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      await api.post("/subscribe", { email: email.trim(), source: slug });
      setStatus("success");
      track("article_subscribed", { source: slug });
      toast.success("You're on the list.", { description: "We'll send the next field note straight to your inbox." });
    } catch (err) {
      setStatus("idle");
      const msg = err?.response?.data?.detail || "Couldn't subscribe right now — try again in a moment.";
      toast.error(msg);
    }
  };
  if (status === "success") {
    return (
      <div className="border border-[#ac62ea]/40 bg-[#ac62ea]/[0.06] rounded-2xl p-7 text-center" data-testid="subscribe-success">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#ac62ea]/20 mb-3">
          <Check className="h-5 w-5 text-[#ac62ea]" strokeWidth={3} />
        </div>
        <div className="font-display text-xl">You&rsquo;re on the list.</div>
        <p className="text-sm text-muted-foreground mt-2">We publish a new field note every few weeks. No spam, ever.</p>
        <button
          type="button"
          onClick={() => openChat({ context: `Post-subscribe assist · ${slug}`, email })}
          className="mt-4 text-sm text-[#ac62ea] hover:underline font-medium"
          data-testid="subscribe-chat-cta"
        >
          Have a question? Chat with us &rarr;
        </button>
      </div>
    );
  }
  return (
    <div className="border border-border rounded-2xl p-7 bg-white" data-testid="subscribe-box">
      <div className="label-mono">Subscribe</div>
      <h3 className="font-display text-xl sm:text-2xl font-semibold mt-2 tracking-tight">Get the next field note in your inbox.</h3>
      <p className="text-sm text-muted-foreground mt-2">A new piece every few weeks on acceptance economics, vendor cost reviews and the agentic-commerce shift. No spam.</p>
      <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-2" data-testid="subscribe-form">
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="rounded-full h-11 px-4"
          data-testid="subscribe-email-input"
          disabled={status === "loading"}
        />
        <Button
          type="submit"
          disabled={status === "loading"}
          className="btn-pill bg-[#0b1e5a] hover:bg-[#0b1e5a]/90 text-white h-11 px-6 whitespace-nowrap"
          data-testid="subscribe-submit"
        >
          {status === "loading" ? "Subscribing…" : "Subscribe"}
        </Button>
      </form>
    </div>
  );
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Re-fetch only when the slug changes; api / setters are stable.
    api.get(`/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="article-not-found">
        <h1 className="font-display text-3xl font-semibold">Article not found</h1>
        <Button onClick={() => navigate("/articles")} className="mt-6 btn-pill bg-[#0b1e5a]">Back to insights</Button>
      </div>
    );
  }
  if (!article) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;

  return (
    <article className="bg-white" data-testid="article-detail">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-12 pb-6">
        <Link to="/articles" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-[#ac62ea] transition-colors" data-testid="article-back">
          <ArrowLeft className="h-4 w-4" /> All insights
        </Link>
      </div>
      <header className="max-w-3xl mx-auto px-6 lg:px-10 py-6">
        <div className="flex items-center gap-3 text-xs">
          <span className="text-[#ac62ea] font-medium uppercase tracking-widest">{article.category}</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-4 tracking-[-0.035em] leading-[1.05]">{article.title}</h1>
        <p className="text-lg text-muted-foreground mt-5 leading-relaxed">{article.subtitle}</p>
        <div className="mt-7 flex items-center gap-3 text-sm">
          <div className="w-9 h-9 rounded-full bg-[#0b1e5a] text-white flex items-center justify-center font-semibold">{article.author[0]}</div>
          <span className="font-medium">{article.author}</span>
        </div>
      </header>
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="aspect-[16/9] rounded-2xl overflow-hidden">
          <ArticleCover article={article} className="w-full h-full" size="lg" />
        </div>
      </div>
      <section className="max-w-2xl mx-auto px-6 lg:px-10 py-14">
        {article.body.split("\n\n").map((p, i) => (
          <p key={`${i}-${p.slice(0, 24)}`} className="text-lg leading-relaxed text-foreground/85 mb-5">{p}</p>
        ))}
      </section>
      <section className="max-w-2xl mx-auto px-6 lg:px-10 pb-14">
        <SubscribeBox slug={slug} />
      </section>
      <section className="border-t border-border bg-[#f6f1fb]">
        <div className="max-w-2xl mx-auto px-6 lg:px-10 py-14 text-center">
          <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.03em]">Want this kind of analysis on your business?</h3>
          <Button asChild className="mt-6 btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] h-11 px-6" data-testid="article-cta">
            <Link to="/services">Book a discovery session <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>
    </article>
  );
}
